export interface AccessInfoNoticeProps {
	/** "global" = Company Wide Access, "specific" = Specific Access (CONTRACT §11's `mode`). */
	mode: "global" | "specific";
}

/**
 * A6 — VERBATIM copy, byte-for-byte from the ticket's "Required copy" section.
 * Do NOT normalise punctuation, casing or spacing. Do NOT apply the C4
 * Global -> Company Wide terminology sweep to this prose — CONTRACT §10.3
 * carves this exact paragraph out: it is frozen ticket copy (A6), which wins
 * over C4 for this string specifically. C4 governs standalone labels/headings
 * only (see `AccessModeSelector`'s "Company Wide Access" card heading).
 *
 * Note deliberately-as-written details that a future edit must not "fix":
 * - the company-wide copy's own lowercase mid-sentence "company-wide"
 * - lowercase "rfx" in the company-wide copy vs capitalised "RFx" in the
 *   specific copy
 * - a hyphen (not an em-dash) after "cascades"
 * - no trailing full stop on the company-wide copy
 */
const COMPANY_WIDE_COPY =
	"Granting company-wide access to a user will provide them with full access for the selected role across all portfolios, projects and rfx existing now and those added in the future";

/**
 * AUTHOR AMENDMENT (2026-09-24, author item 3.3) — A6/CONTRACT §10.3 froze this exact sentence
 * on the MODE step's Specific card. The author has since moved where it lives (now the TOP of
 * the level-selection/picker step — see `ScopePicker`'s own `data-testid="scope-cascade-notice"`)
 * and replaced it on this screen with the short "click next" prompt below, because the picker no
 * longer renders on this same step (it moved to a later step in D8/D10's flow, so "from the
 * options below" pointed at nothing). The author's instruction wins over the frozen copy per the
 * coordinator's recorded amendment; this string is exported so `ScopePicker` renders the exact
 * same verbatim text it always was, just relocated, not reworded.
 */
export const CASCADE_COPY =
	"Please select different levels of access for this user from the options below. Security access cascades - For example, if you pick a portfolio they have access to all projects and RFx within that Portfolio.";

/**
 * PRO-757 follow-up — the bidder-only variant of {@link CASCADE_COPY}.
 *
 * `CASCADE_COPY` is frozen ticket copy, and it explains the cascade by example:
 * "if you pick a portfolio they have access to all projects and RFx within that
 * Portfolio". A bidder-only company has no portfolios and no projects (they are
 * procurer-owned), so on that company's picker the sentence describes a control
 * that isn't there and a cascade that cannot happen. This says the true thing
 * for the one list they do get.
 *
 * `CASCADE_COPY` itself is untouched — it is still exactly what a procurer or a
 * hybrid sees, byte for byte.
 */
export const RFX_ONLY_COPY =
	"Select the RFx events this user should have the role for. Your company is invited to these events rather than running them, so access is granted one event at a time.";

/**
 * PRO-757 follow-up — the BIDDER-ROLE variant of {@link RFX_ONLY_COPY}.
 *
 * Author: "if a hybrid or bidder company select a bidder role only let RFx's
 * they have been invited to be displayed, not projects."
 *
 * On a hybrid company {@link RFX_ONLY_COPY} would be a false statement: that
 * company DOES run its own events, and it really does own the portfolios and
 * projects the picker has just stopped showing. The reason the lists are gone
 * here is the ROLE, not the company, so the sentence names the role instead.
 *
 * A bidder-only company keeps {@link RFX_ONLY_COPY}: both narrowings apply
 * there, and the company one is the more informative explanation of why it has
 * no portfolios to pick in the first place.
 */
export const BIDDER_ROLE_RFX_ONLY_COPY =
	"Select the RFx events this user should have the role for. Bidder roles apply to a single RFx event at a time, so they cannot be granted across a portfolio or a project.";

const SPECIFIC_MODE_COPY = "Click next to select access levels";

// Renders the required-copy info block for the current Add-flow mode (D8-T1, A6).
export const AccessInfoNotice = ({ mode }: AccessInfoNoticeProps) => (
	<div
		className="rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm leading-relaxed text-blue-900"
		data-testid="access-info-notice"
		role="note"
	>
		{mode === "global" ? COMPANY_WIDE_COPY : SPECIFIC_MODE_COPY}
	</div>
);
